import React from 'react';
import { compose } from 'redux';
import { injectIntl } from 'react-intl';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _isArray from 'lodash/isArray';
import _isEmpty from 'lodash/isEmpty';
import _isFunction from 'lodash/isFunction';
import _isObject from 'lodash/isObject';
import _isNil from 'lodash/isNil';
import _isString from 'lodash/isString';
import _isNumber from 'lodash/isNumber';
import _join from 'lodash/join';
import _map from 'lodash/map';
import _mapValues from 'lodash/mapValues';
import _random from 'lodash/random';
import _reduce from 'lodash/reduce';
import _set from 'lodash/set';
import _transform from 'lodash/transform';
import PropTypes from 'prop-types';
// Custom
import { FormRefContext } from 'components/misc/Contexts';
import { getHocDisplayName } from 'utils/Tools';
import { computeErrors } from 'utils/Forms';
import withScrollerManagement from './WithScrollerManagement';

const withFormManagement = (mapFieldsToProps, mapOwnPropsToResources, withErrorDisplayer = true, additionalErrorPaths = []) => (WrappedComponent, options = {}) => {
  const { withRef = false } = options;

  const parseOwnPropsToResources = (ownProps) => {
    if (_isFunction(mapOwnPropsToResources)) {
      return mapOwnPropsToResources(ownProps);
    }
    return {};
  };

  class WithFormManagement extends React.Component {
    constructor(props) {
      super(props);

      this.handleFieldsChange = this.handleFieldsChange.bind(this);
      this.handleErrorsChange = this.handleErrorsChange.bind(this);
      this.handleReset = this.handleReset.bind(this);

      // Create a reference for this form
      this.formRef = `form_${_random(10000, 99999)}`;

      this.state = {
        localFields: {},
        localErrors: null,
        isResetDisabled: true,
      };
    }

    componentDidUpdate(prevProps) {
      // Check if there is any new operation error
      const { operationError: prevOperationError } = parseOwnPropsToResources(prevProps);
      const { operationError } = parseOwnPropsToResources(this.props);
      // Scroll to error displayer if there is any error
      if (!prevOperationError && operationError) {
        this.doScrollToErrorDisplayer();
      }
    }

    getWrappedInstance() {
      return withRef ? this.wrappedInstance : null;
    }

    // Handlers
    handleFieldsChange(newFields, callback) {
      this.setState((state) => {
        const updatedFields = {
          ...state.localFields,
          ...newFields,
        };

        // Check if the fields have changed
        const hasChanges = Object.keys(updatedFields).some(
          (key) => updatedFields[key] !== state.localFields[key],
        );

        return {
          localFields: updatedFields,
          isResetDisabled: !hasChanges,
        };
      }, callback);
    }

    handleErrorsChange(errors) {
      this.setState({
        localErrors: errors,
      });

      // Scroll to error displayer if there is any error
      if (errors) {
        this.doScrollToErrorDisplayer();
      }
    }

    handleReset() {
      this.setState({
        localFields: {},
        localErrors: null,
        isResetDisabled: true,
      });
    }

    // Private
    doScrollToErrorDisplayer() {
      if (withErrorDisplayer) {
        const { handleScrollTo } = this.props;
        handleScrollTo(this.formRef, 'errorDisplayer');
      }
    }

    doFormatErrorsMessages(errors) {
      if (_isEmpty(errors)) {
        return null;
      }

      // We must generate formatted messages for each error (and keep the `global` attribute)
      return _transform(errors.details, (result, value, key) => {
        _set(result, key, this.doGetFormattedErrorMessages(value, key));
      }, {
        global: errors.global,
      });
    }

    doGetFormattedErrorMessages(messagesOrChildren, key) {
      const { intl } = this.props;
      // messagesOrChildren is an array of messages => return them translated
      if (_isArray(messagesOrChildren)) {
        return _join(
          _map(messagesOrChildren, (msg) => {
            // Message is either:
            //  - A string => id of the message
            //  - An object like { id, values }, in case of dynamic values to be injected
            if (_isObject(msg)) {
              const { id, values } = msg;
              // Values is an object like:
              // {
              //   valueKey: {
              //     value: any // The base value
              //     setTransform: function // An optionnal function to transform value. Args: (value, ownProps)
              //   }
              // }
              return intl.formatMessage(
                { id: `formError.${key}.${id}` },
                _transform(values, (result, valueObj, valueKey) => {
                  const { value, setTransform } = valueObj;
                  const computedValue = _isFunction(setTransform) ? setTransform(value, this.props) : value;
                  // In case we have a computed value with nested dynamic variables,
                  // we want to flatten it and assign each nested to the result
                  if (_isObject(computedValue)) {
                    Object.entries(computedValue).forEach(([nestedKey, nestedValue]) => _set(result, nestedKey, nestedValue));
                  } else {
                    _set(result, valueKey, computedValue);
                  }
                  return result;
                }, {}),
              );
            }
            return intl.formatMessage({ id: `formError.${key}.${msg}` });
          }), `,${' '}`,
        );
      }
      // messagesOrChildren is an object: translate its children
      return _transform(messagesOrChildren, (result, childValue, childKey) => {
        _set(result, childKey, this.doGetFormattedErrorMessages(childValue, childKey));
        return result;
      }, {});
    }

    render() {
      const { localFields, localErrors, isResetDisabled } = this.state;

      // Retrieve initial resource, and operation error, if any
      const { resourceData, operationError } = parseOwnPropsToResources(this.props);

      // Build final fields
      const fields = _mapValues(mapFieldsToProps, (value, key) => {
        const { resourceKey, defaultValue, setTransform } = value;

        // Check if key exists in local state
        const localValueExists = _has(localFields, key);
        // Check if key exists in resource structure
        const resourceValueExists = _has(resourceData, resourceKey);

        // Priority to local stored value, then pick in resource and if nothing was found, take default
        if (localValueExists) {
          return _get(localFields, key);
        }
        if (resourceValueExists) {
          // Get value from resource (used in form initialization, may be undefined)
          let resourceValue = _get(resourceData, resourceKey);
          // Apply transformation to this value if needed
          if (!_isNil(setTransform) && !_isNil(resourceValue)) {
            resourceValue = setTransform(resourceValue);
          }
          // Case of textfields that can not be initialized to null
          if (_isNil(resourceValue) && (_isString(defaultValue) || _isNumber(defaultValue))) {
            resourceValue = defaultValue;
          }
          return resourceValue;
        }
        return defaultValue;
      });

      // Compute errors and generate formatted messages
      const formattedErrors = this.doFormatErrorsMessages(computeErrors(localErrors, operationError));

      // Build additional errors
      const additionalError = _reduce(additionalErrorPaths, (result, path) => {
        // Try to find in formatted errors an error for this path
        const error = _get(formattedErrors, path);
        if (error && _isString(error)) {
          result.push(error);
        }
        return result;
      }, []);

      return (
        <FormRefContext.Provider value={this.formRef}>
          <WrappedComponent
            fields={fields}
            errors={formattedErrors}
            additionalError={additionalError}
            onFieldsChange={this.handleFieldsChange}
            onErrorsChange={this.handleErrorsChange}
            onResetForm={this.handleReset}
            isResetDisabled={isResetDisabled}
            ref={withRef ? (wi) => { this.wrappedInstance = wi; } : null}
            {...this.props}
          />
        </FormRefContext.Provider>
      );
    }
  }

  WithFormManagement.displayName = getHocDisplayName('WithFormManagement', WrappedComponent);

  WithFormManagement.propTypes = {
    intl: PropTypes.object.isRequired,
    handleScrollTo: PropTypes.func.isRequired,
  };

  return compose(
    injectIntl,
    withScrollerManagement,
  )(WithFormManagement);
};

export default withFormManagement;



// WEBPACK FOOTER //
// ./hocs/WithFormManagement.js